import { C } from '../../lib/landingColors'
import { PREVAILING_LANGUAGE_NOTICE_LABEL } from '../../content/legalCopy'
import {
  MarketingHeading,
  MarketingSubheading,
  MarketingParagraph,
  MarketingList,
  MarketingListItem,
  MarketingLink,
} from '../../components/MarketingShell'

// The German Terms & Conditions. The English version at /terms is the binding
// one — clause 16 of both versions says so, and the notice above section 1
// repeats it for a reader who starts here.
//
// The translation is deliberately literal. Where the English is ambiguous the
// German reproduces the ambiguity rather than resolving it: tidying a clause in
// one language only is how two versions start meaning different things. The
// open questions found while translating are listed in
// agentic-testing-harness/runs/p4-fix-sweep/W14-legal-de.md — they are
// questions about the ENGLISH text, and fixing them fixes both.
//
// Swiss German orthography: no Eszett anywhere, "ss" throughout.

/**
 * The German Terms & Conditions — a convenience translation, not the binding
 * text.
 *
 * Structurally identical to `TermsBodyEn` on purpose: same fifteen numbered
 * sections plus clause 16, same 5.1-5.5 and 9.1-9.3 subsections, same
 * cross-references, same inline links (pointed at the German siblings).
 * `__tests__/legalTranslationParity.test.tsx` fails the build if that stops
 * being true, which is what makes "we maintain a translation" a mechanical
 * claim rather than a promise.
 */
export function TermsBodyDe() {
  return (
    <>
      {/* The clause a German reader has to meet FIRST — clause 16 says the same
          thing, but at the bottom of a 16-section contract. */}
      <aside
        role="note"
        aria-label={PREVAILING_LANGUAGE_NOTICE_LABEL}
        className="rounded-xl p-4 mb-8 text-sm leading-relaxed"
        style={{ background: `${C.primary}0c`, border: `1px solid ${C.primary}25`, color: C.muted }}
      >
        Diese deutsche Fassung ist eine Übersetzung und dient ausschliesslich
        dem besseren Verständnis. Verbindlich ist die englische Fassung: Bei
        Widersprüchen oder Auslegungsunterschieden zwischen den beiden Fassungen
        ist die englische Fassung massgebend. Sie finden sie unter{' '}
        <MarketingLink to="/terms">/terms</MarketingLink>. Siehe auch Ziffer 16.
      </aside>

      {/* ── 1. Leistungsbeschreibung ────────────────────────────── */}
      <section>
        <MarketingHeading>1. Leistungsbeschreibung</MarketingHeading>
        <MarketingParagraph>
          StudioFlow ist eine cloudbasierte Plattform zur Studioverwaltung für
          professionelle Foto- und Videostudios. Sie bietet Werkzeuge für
          Projektverwaltung, Kundenverwaltung, Terminplanung, Rechnungsstellung,
          Auslieferung von Kundengalerien, Zusammenarbeit im Team und verwandte
          Arbeitsabläufe. Die Plattform wird von StudioFlow («wir», «uns»,
          «unser») betrieben und ist über den Webbrowser sowie, soweit
          verfügbar, über mobile Oberflächen zugänglich.
        </MarketingParagraph>
      </section>

      {/* ── 2. Konto & Zugang ───────────────────────────────────── */}
      <section>
        <MarketingHeading>2. Konto &amp; Zugang</MarketingHeading>
        <MarketingParagraph>
          Jedes Benutzerkonto ist persönlich und darf nicht geteilt werden. Sie
          sind für die Geheimhaltung Ihrer Anmeldedaten und für sämtliche
          Aktivitäten unter Ihrem Konto verantwortlich. Sie müssen uns jede
          unbefugte Nutzung unverzüglich melden. Wir behalten uns das Recht vor,
          ein Konto zu deaktivieren, wenn wir begründet annehmen, dass die
          Anmeldedaten kompromittiert wurden.
        </MarketingParagraph>
        <MarketingParagraph>
          Sie dürfen kein Konto im Namen einer anderen Person oder Organisation
          ohne deren Ermächtigung eröffnen und keine mehreren Konten anlegen, um
          Limiten des Abonnements, Fair-Use-Schwellen oder Einschränkungen nach
          diesen AGB zu umgehen.
        </MarketingParagraph>
      </section>

      {/* ── 3. Dateneigentum ────────────────────────────────────── */}
      <section>
        <MarketingHeading>3. Dateneigentum</MarketingHeading>
        <MarketingParagraph>
          Sämtliche Inhalte, die Sie auf die Plattform hochladen oder dort
          erstellen, bleiben vollumfänglich in Ihrem Eigentum, insbesondere:
          Fotografien, Videos, Dokumente, Kundendaten, Rechnungen, Verträge und
          Projektinformationen. Wir erheben keinerlei Immaterialgüterrechte an
          Ihren Inhalten. Die Plattform speichert und bearbeitet Ihre Daten
          ausschliesslich, um den Dienst zu erbringen und zu verbessern.
        </MarketingParagraph>
        <MarketingParagraph>
          Sie räumen uns eine beschränkte, nicht ausschliessliche Lizenz ein,
          Ihre Inhalte zu hosten, zu speichern, zwischenzuspeichern und zu
          übertragen, soweit dies für den Betrieb des Dienstes vernünftigerweise
          erforderlich ist (z. B. Erzeugen von Vorschaubildern, Ausliefern von
          Kundengalerien, Erstellen von Sicherungskopien).
        </MarketingParagraph>
      </section>

      {/* ── 4. Abonnemente, Rechnungsstellung & Rückerstattungen ── */}
      <section>
        <MarketingHeading>4. Abonnemente, Rechnungsstellung &amp; Rückerstattungen</MarketingHeading>
        <MarketingParagraph>
          StudioFlow bietet Abonnemente mit unterschiedlichen Kontingenten für
          Speicherplatz, Teammitglieder und Funktionen an. Die Abrechnung
          erfolgt monatlich oder jährlich in Schweizer Franken (CHF) über
          unseren Zahlungsdienstleister Stripe. Alle Preise verstehen sich
          inklusive der anwendbaren schweizerischen Mehrwertsteuer (MWST),
          sofern nichts anderes angegeben ist.
        </MarketingParagraph>
        <MarketingParagraph>
          Konten in der kostenlosen Testphase haben während 30 Tagen vollen
          Zugriff auf alle Funktionen. Nach Ablauf der Testphase müssen Sie ein
          kostenpflichtiges Abonnement wählen, um die Plattform weiter zu
          nutzen. Wird kein Abonnement gewählt, wird Ihr Konto herabgestuft und
          der Zugriff auf bestimmte Funktionen eingeschränkt, bis ein Abonnement
          aktiviert wird; Ihre Daten werden nach Ablauf der Testphase während
          mindestens 60 Tagen aufbewahrt.
        </MarketingParagraph>
        <MarketingParagraph>
          Abonnemente verlängern sich am Ende jeder Abrechnungsperiode
          automatisch, sofern sie nicht vorher gekündigt werden. Sie können Ihr
          Abonnement jederzeit über die Einstellungsseite oder über das
          Stripe-Kundenportal kündigen oder wechseln. Kündigungen werden auf das
          Ende der laufenden Abrechnungsperiode wirksam.
        </MarketingParagraph>
        <MarketingParagraph>
          Zusatzleistungen (z. B. Speichererweiterungen) werden als separate
          wiederkehrende Gebühren abgerechnet und können unabhängig vom
          Hauptabonnement verwaltet werden.
        </MarketingParagraph>
        <MarketingParagraph>
          Rückerstattungen werden im Einzelfall geprüft. Wenn Sie der Ansicht
          sind, dass Ihnen ein Betrag zu Unrecht belastet wurde, kontaktieren
          Sie uns bitte innerhalb von 14 Tagen nach der Belastung.
        </MarketingParagraph>
      </section>

      {/* ── 5. Fair-Use- & Speicherrichtlinie ───────────────────── */}
      <section>
        <MarketingHeading>5. Fair-Use- &amp; Speicherrichtlinie</MarketingHeading>
        <MarketingParagraph>
          StudioFlow ist für den Betrieb professioneller Foto- und Videostudios
          ausgelegt. Alle Limiten des Abonnements — einschliesslich
          Speicherplatz, Teammitglieder und API-Zugriff — unterliegen dieser
          Fair-Use-Richtlinie.
        </MarketingParagraph>

        <MarketingSubheading>5.1 Speicherplatz</MarketingSubheading>
        <MarketingParagraph>
          Jedes Abonnement enthält ein Grundkontingent an Speicherplatz. Der
          Speicherverbrauch entspricht der Summe aller auf der Plattform
          gespeicherten Dateien, einschliesslich Galeriefotos, Chat-Anhängen und
          hochgeladenen Dokumenten. Agency-Abonnemente können zusätzlichen
          Speicher in Schritten von 1 TB dazukaufen. Dateien, die Sie auf der
          Plattform löschen, werden umgehend aus Ihrem Kontingent freigegeben
          und anschliessend nach unserem üblichen Aufbewahrungsplan endgültig
          aus dem Objektspeicher entfernt.
        </MarketingParagraph>

        <MarketingSubheading>5.2 Auslieferung von Kundengalerien</MarketingSubheading>
        <MarketingParagraph>
          Das Betrachten von Kundengalerien, Album-Downloads und die
          Zusammenarbeit im Team sind in allen Abonnementen ohne zusätzliche
          verbrauchsabhängige Kosten enthalten. StudioFlow liefert
          Galerieinhalte über ein Content Delivery Network (CDN) aus, dessen
          Bandbreite nach der aktuellen Preisgestaltung nicht separat verrechnet
          wird. Wir behalten uns vor, künftig eine verbrauchsabhängige Messung,
          Zusatzgebühren oder eine Drosselung einzuführen, falls das
          Auslieferungsvolumen einer bestimmten Organisation wesentlich vom
          normalen Studiobetrieb abweicht; jede solche Änderung, die bestehende
          Abonnemente betrifft, wird gemäss Ziffer 12 vorgängig mitgeteilt.
        </MarketingParagraph>

        <MarketingSubheading>5.3 Unzulässige Nutzungsmuster</MarketingSubheading>
        <MarketingParagraph>Sie verpflichten sich, Folgendes zu unterlassen:</MarketingParagraph>
        <MarketingList>
          <MarketingListItem>
            Die Plattform als allgemeinen Datei-, Sicherungs- oder
            Archivspeicher zu nutzen, der nichts mit dem Betrieb eines Foto-
            oder Videostudios zu tun hat
          </MarketingListItem>
          <MarketingListItem>
            Wiederholte Zyklen von Hochladen und Löschen zu betreiben, um
            Speicherlimiten zu umgehen oder Abrechnungsstrukturen der
            Infrastruktur auszunutzen
          </MarketingListItem>
          <MarketingListItem>
            Automatisierte Werkzeuge, Skripte oder Bots einzusetzen, um Inhalte
            in grossem Umfang ausserhalb der dafür vorgesehenen Oberflächen der
            Plattform hoch- oder herunterzuladen oder zu löschen
          </MarketingListItem>
          <MarketingListItem>
            Download-Links zu Galerien öffentlich zu teilen oder die massenhafte
            Weiterverbreitung von Inhalten in einer Weise zu ermöglichen, die
            einer normalen Kundenauslieferung widerspricht
          </MarketingListItem>
          <MarketingListItem>
            Mehrere Organisationen oder Konten anzulegen, um Limiten des
            Abonnements zu umgehen
          </MarketingListItem>
        </MarketingList>

        <MarketingSubheading>5.4 Überwachung &amp; Durchsetzung</MarketingSubheading>
        <MarketingParagraph>
          Wir überwachen aggregierte Nutzungsmuster, um allen Nutzenden einen
          fairen Zugang zu sichern. Die Inhalte Ihrer Dateien sehen wir uns
          dabei nicht an. Stellen wir Nutzungsmuster fest, die mit einem
          normalen Studiobetrieb unvereinbar erscheinen, kontaktieren wir die
          Inhaberin oder den Inhaber der Organisation, um die Situation zu
          besprechen, bevor wir Massnahmen ergreifen. Setzt sich ein
          missbräuchliches Muster nach dieser Mitteilung fort, behalten wir uns
          vor, den Zugriff zu drosseln, Zusatzgebühren zu erheben oder die
          Organisation gemäss Ziffer 9 zu sperren.
        </MarketingParagraph>

        <MarketingSubheading>5.5 Foto- und Videospeicherung ist kein Sicherungsdienst</MarketingSubheading>
        <MarketingParagraph>
          StudioFlow speichert die von Ihnen hochgeladenen Originaldateien in
          einem Cloud-Objektspeicher und liefert sie an Ihre Kundschaft und Ihr
          Team aus. Das ist
          <em> operativer</em> Speicher, kein Archiv-Backup. Wir treffen
          wirtschaftlich angemessene Massnahmen zum Schutz ruhender Daten
          (einschliesslich Redundanz und Versionierung auf Ebene des
          Speicheranbieters — siehe Ziffer 8), garantieren jedoch nicht gegen
          den dauerhaften Verlust hochgeladener Medien bei Ereignissen wie einem
          katastrophalen Ausfall des Anbieters, einer regionalen Katastrophe,
          einer Kompromittierung des Kontos oder einer versehentlichen Löschung,
          die innerhalb des Aufbewahrungsfensters des Anbieters nicht
          wiederherstellbar ist.
        </MarketingParagraph>
        <MarketingParagraph>
          <strong>Sie sind allein dafür verantwortlich, eine eigene,
          unabhängige Sicherungskopie Ihrer RAW-Originaldateien und aller
          weiteren Originaldaten zu führen, die Sie als unersetzlich
          betrachten.</strong> Wir empfehlen dringend, mindestens eine
          Offline-Kopie oder eine geografisch getrennte Kopie solcher Dateien
          unter Ihrer direkten Kontrolle aufzubewahren. Unsere Haftung für den
          Verlust hochgeladener Medien ist gemäss Ziffer 11 beschränkt und
          übersteigt die dort genannten Beträge unabhängig von der Ursache
          nicht.
        </MarketingParagraph>
      </section>

      {/* ── 6. Zulässige Nutzung ────────────────────────────────── */}
      <section>
        <MarketingHeading>6. Zulässige Nutzung</MarketingHeading>
        <MarketingParagraph>
          Zusätzlich zur vorstehenden Fair-Use-Richtlinie verpflichten Sie sich,
          Folgendes zu unterlassen:
        </MarketingParagraph>
        <MarketingList>
          <MarketingListItem>Rechtswidrige, ehrverletzende oder rechtsverletzende Inhalte
            hochzuladen, zu speichern oder zu verbreiten</MarketingListItem>
          <MarketingListItem>Inhalte hochzuladen, zu deren Speicherung oder Verbreitung Sie
            nicht berechtigt sind</MarketingListItem>
          <MarketingListItem>Den Zugang zur Plattform weiterzuverkaufen, unterzulizenzieren
            oder weiterzugeben</MarketingListItem>
          <MarketingListItem>Automatisierte Werkzeuge einzusetzen, um Daten der Plattform
            auszulesen, zu crawlen oder zu extrahieren, oder Teile des Dienstes
            zurückzuentwickeln</MarketingListItem>
          <MarketingListItem>Zu versuchen, unbefugten Zugang zu Konten, Organisationen oder
            Daten anderer Nutzenden zu erlangen</MarketingListItem>
          <MarketingListItem>Die Plattform in einer Weise zu nutzen, die den Dienst
            beschädigen, ausser Betrieb setzen, überlasten oder beeinträchtigen
            oder den Zugang anderer Nutzenden stören könnte</MarketingListItem>
          <MarketingListItem>Viren, Schadsoftware oder anderen schädlichen Code
            einzuschleusen</MarketingListItem>
          <MarketingListItem>Unerwünschte Nachrichten (Spam) über die Nachrichten- oder
            E-Mail-Funktionen der Plattform zu versenden</MarketingListItem>
        </MarketingList>
      </section>

      {/* ── 7. Geistiges Eigentum ───────────────────────────────── */}
      <section>
        <MarketingHeading>7. Geistiges Eigentum</MarketingHeading>
        <MarketingParagraph>
          Software, Gestaltung, Logos und Dokumentation der Plattform sind
          geistiges Eigentum von StudioFlow. Ihre Inhalte bleiben Ihr Eigentum.
          Diese Vereinbarung überträgt keine Immaterialgüterrechte zwischen den
          Parteien, ausser wo dies ausdrücklich festgehalten ist.
        </MarketingParagraph>
      </section>

      {/* ── 8. Datenverarbeitung & Sicherheit ───────────────────── */}
      <section>
        <MarketingHeading>8. Datenverarbeitung &amp; Sicherheit</MarketingHeading>
        <MarketingParagraph>
          Anwendungsdaten (Datenbank, Applikationsserver) werden in der
          Europäischen Union gehostet (Hetzner, Deutschland). Foto- und
          Videodateien werden im Objektspeicher Backblaze B2 in einer EU-Region
          abgelegt und über das Content Delivery Network von Cloudflare an
          Browser ausgeliefert. Alle Daten werden auf dem Übertragungsweg mit
          TLS verschlüsselt. Sicherungen der Datenbank erfolgen täglich. Foto-
          und Videodateien sind auf Ebene des Speicheranbieters durch
          Bucket-Versionierung geschützt; wie in Ziffer 5.5 festgehalten,
          ersetzt dieser operative Speicher jedoch nicht Ihre eigene,
          unabhängige Archivsicherung der Originaldaten.
        </MarketingParagraph>
        <MarketingParagraph>
          Wir setzen wirtschaftlich angemessene technische und organisatorische
          Massnahmen ein, um Ihre Daten vor unbefugtem Zugriff, Veränderung oder
          Zerstörung zu schützen. Keine Methode der elektronischen Übertragung
          oder Speicherung ist jedoch zu 100 % sicher, und wir können keine
          absolute Sicherheit garantieren.
        </MarketingParagraph>
        <MarketingParagraph>
          Einzelheiten dazu, wie wir Ihre Daten erheben, bearbeiten und
          behandeln, finden Sie in unserer{' '}
          <MarketingLink to="/de/privacy">Datenschutzerklärung</MarketingLink>.
        </MarketingParagraph>
      </section>

      {/* ── 9. Sperrung & Kündigung ─────────────────────────────── */}
      <section>
        <MarketingHeading>9. Sperrung &amp; Kündigung</MarketingHeading>

        <MarketingSubheading>9.1 Durch Sie</MarketingSubheading>
        <MarketingParagraph>
          Sie können Ihr Abonnement jederzeit über die Einstellungsseite
          kündigen und die Löschung Ihres Kontos verlangen. Inhaberinnen und
          Inhaber einer Organisation können ihre gesamte Organisation samt allen
          zugehörigen Daten löschen. Nach der Löschung entfernen wir Ihre Daten
          innerhalb von 30 Tagen endgültig, ausser wo eine Aufbewahrung
          gesetzlich vorgeschrieben ist.
        </MarketingParagraph>

        <MarketingSubheading>9.2 Durch uns</MarketingSubheading>
        <MarketingParagraph>
          Wir behalten uns vor, Ihr Konto oder Ihre Organisation zu sperren oder
          zu kündigen, wenn:
        </MarketingParagraph>
        <MarketingList>
          <MarketingListItem>Sie gegen diese Allgemeinen Geschäftsbedingungen, die
            Fair-Use-Richtlinie oder die Richtlinie zur zulässigen Nutzung
            verstossen</MarketingListItem>
          <MarketingListItem>festgestellt wird, dass Ihre Nutzung rechtswidrige, unzulässige,
            ungewöhnliche oder missbräuchliche Aktivitäten umfasst</MarketingListItem>
          <MarketingListItem>Ihr Konto seit mehr als 12 aufeinanderfolgenden Monaten in einem
            kostenlosen oder abgelaufenen Testabonnement inaktiv ist</MarketingListItem>
          <MarketingListItem>die Zahlung für Ihr Abonnement wiederholt fehlschlägt und nach
            einer Benachrichtigung ungelöst bleibt</MarketingListItem>
        </MarketingList>
        <MarketingParagraph>
          Soweit praktikabel, teilen wir der Inhaberin oder dem Inhaber der
          Organisation eine Sperrung oder Kündigung mindestens 14 Tage im
          Voraus schriftlich mit; ausgenommen sind Fälle schweren Missbrauchs,
          gesetzlicher Verpflichtungen oder einer Gefährdung anderer Nutzenden
          oder der Infrastruktur, in denen wir sofort handeln können.
        </MarketingParagraph>

        <MarketingSubheading>9.3 Wirkung der Kündigung</MarketingSubheading>
        <MarketingParagraph>
          Mit der Kündigung oder Sperrung endet Ihr Recht zur Nutzung des
          Dienstes mit sofortiger Wirkung. Wir können Ihr Konto und alle
          zugehörigen Daten deaktivieren oder löschen. Bei Konten, die wir wegen
          Verstössen gegen die Richtlinien gekündigt haben, sind wir nicht
          verpflichtet, eine Kopie Ihrer Daten bereitzustellen; wir bemühen uns
          jedoch in angemessenem Rahmen, einen Datenexport zu ermöglichen, wo
          die Umstände es zulassen. Bestimmungen dieser AGB, die ihrer Natur
          nach die Beendigung überdauern sollen (einschliesslich Dateneigentum,
          Haftungsbeschränkung, Schadloshaltung und anwendbares Recht), gelten
          weiter.
        </MarketingParagraph>
      </section>

      {/* ── 10. Schadloshaltung ─────────────────────────────────── */}
      <section>
        <MarketingHeading>10. Schadloshaltung</MarketingHeading>
        <MarketingParagraph>
          Sie verpflichten sich, StudioFlow sowie deren Organe,
          Führungspersonen, Mitarbeitende und Beauftragte von allen Ansprüchen,
          Verbindlichkeiten, Schäden, Verlusten und Aufwendungen (einschliesslich
          angemessener Anwaltskosten) freizustellen, schadlos zu halten und zu
          verteidigen, die sich ergeben aus oder im Zusammenhang stehen mit: (a)
          Ihrer Nutzung der Plattform, (b) Ihrem Verstoss gegen diese AGB, (c)
          Ihrer Verletzung von Rechten Dritter einschliesslich
          Immaterialgüterrechten oder (d) Inhalten, die Sie auf die Plattform
          hochladen oder über sie übermitteln.
        </MarketingParagraph>
      </section>

      {/* ── 11. Haftungsbeschränkung ────────────────────────────── */}
      <section>
        <MarketingHeading>11. Haftungsbeschränkung</MarketingHeading>
        <MarketingParagraph>
          Die Plattform wird «wie besehen» zur Verfügung gestellt, ohne
          ausdrückliche oder stillschweigende Gewährleistungen jeglicher Art,
          insbesondere ohne Gewährleistung der Marktgängigkeit, der Eignung für
          einen bestimmten Zweck und der Nichtverletzung von Rechten Dritter.
          Wir garantieren keine unterbrechungsfreie oder fehlerfreie
          Verfügbarkeit des Dienstes.
        </MarketingParagraph>
        <MarketingParagraph>
          Soweit nach anwendbarem Recht zulässig, haften wir nicht für
          indirekte, beiläufige oder besondere Schäden, Folgeschäden oder
          Strafschadenersatz, die sich aus Ihrer Nutzung der Plattform ergeben
          oder damit zusammenhängen, insbesondere nicht für Datenverlust,
          Umsatzeinbussen oder Betriebsunterbrüche. Unsere gesamte Haftung für
          sämtliche Ansprüche übersteigt den Betrag nicht, den Sie in den zwölf
          (12) Monaten unmittelbar vor dem schadensauslösenden Ereignis für den
          Dienst bezahlt haben.
        </MarketingParagraph>
      </section>

      {/* ── 12. Änderungen der AGB ──────────────────────────────── */}
      <section>
        <MarketingHeading>12. Änderungen der AGB</MarketingHeading>
        <MarketingParagraph>
          Wir können diese Allgemeinen Geschäftsbedingungen von Zeit zu Zeit
          anpassen. Bei wesentlichen Änderungen benachrichtigen wir die
          Inhaberin oder den Inhaber der Organisation mindestens 30 Tage im
          Voraus per E-Mail. Zudem aktualisieren wir das Datum der letzten
          Aktualisierung oben auf dieser Seite. Die fortgesetzte Nutzung der
          Plattform nach dem Wirksamwerden der Änderungen gilt als Ihre
          Zustimmung zu den geänderten Bedingungen. Sind Sie mit den Änderungen
          nicht einverstanden, können Sie Ihr Abonnement kündigen und Ihr Konto
          löschen, bevor die Änderungen wirksam werden.
        </MarketingParagraph>
      </section>

      {/* ── 13. Anwendbares Recht & Gerichtsstand ───────────────── */}
      <section>
        <MarketingHeading>13. Anwendbares Recht &amp; Gerichtsstand</MarketingHeading>
        <MarketingParagraph>
          Diese AGB unterstehen schweizerischem Recht und werden nach diesem
          ausgelegt, unter Ausschluss der Kollisionsnormen. Für alle
          Streitigkeiten aus oder im Zusammenhang mit diesen AGB sind
          ausschliesslich die Gerichte in Zürich, Schweiz, zuständig.
        </MarketingParagraph>
      </section>

      {/* ── 14. Salvatorische Klausel ───────────────────────────── */}
      <section>
        <MarketingHeading>14. Salvatorische Klausel</MarketingHeading>
        <MarketingParagraph>
          Sollte eine Bestimmung dieser AGB von einem zuständigen Gericht als
          ungültig oder undurchsetzbar beurteilt werden, bleiben die übrigen
          Bestimmungen vollumfänglich in Kraft. Die ungültige Bestimmung wird
          durch eine gültige Bestimmung ersetzt, die der ursprünglichen Absicht
          am nächsten kommt.
        </MarketingParagraph>
      </section>

      {/* ── 15. Kontakt ─────────────────────────────────────────── */}
      <section>
        <MarketingHeading>15. Kontakt</MarketingHeading>
        <MarketingParagraph>
          Bei Fragen zu diesen Allgemeinen Geschäftsbedingungen erreichen Sie
          uns unter{' '}
          <MarketingLink to="mailto:legal@studioflow.ch">legal@studioflow.ch</MarketingLink>
          .
        </MarketingParagraph>
      </section>

      {/* ── 16. Sprache ─────────────────────────────────────────── */}
      <section>
        <MarketingHeading>16. Sprache</MarketingHeading>
        <MarketingParagraph>
          Diese Allgemeinen Geschäftsbedingungen werden in englischer und in
          deutscher Sprache veröffentlicht. Verbindlich ist die englische
          Fassung; die deutsche Fassung dient ausschliesslich dem besseren
          Verständnis. Bei Widersprüchen, Unstimmigkeiten oder
          Auslegungsunterschieden zwischen den beiden Fassungen ist die
          englische Fassung massgebend. Die englische Fassung finden Sie unter{' '}
          <MarketingLink to="/terms">/terms</MarketingLink>.
        </MarketingParagraph>
      </section>

      {/* Der Fussbereich führt diesen Link inzwischen ebenfalls, aber die
          Querverbindung zwischen den beiden Rechtsdokumenten bleibt dort, wo
          eine Leserin am Ende des einen sie erwartet. */}
      <div className="mt-12 pt-6 text-sm" style={{ borderTop: `1px solid ${C.border}` }}>
        <MarketingLink to="/de/privacy">Datenschutzerklärung</MarketingLink>
      </div>
    </>
  )
}
